import React from "react"
import cx from "classnames"
import { usePageContext } from "../page/page-context"
import { PageSecondaryHeaderHeading } from "./page-secondary-header-heading"
import { PageSecondaryHeaderBreadcrumbs } from "./page-secondary-header-breadcrumbs"
import { PageSecondaryHeaderBreadcrumb } from "./page-secondary-header-breadcrumb"
import { PageSecondaryHeaderSteps } from "./page-secondary-header-steps"
import { PageSecondaryHeaderStep } from "./page-secondary-header-step"
import { PageSecondaryHeaderCenterContent } from "./page-secondary-header-center-content"
import { PageSecondaryHeaderActions } from "./page-secondary-header-actions"
import { PageSecondaryHeaderTabs } from "./page-secondary-header-tabs"
import { PageSecondaryHeaderTab } from "./page-secondary-header-tab"
import { Tabs as TabsPrimitive } from "@base-ui/react"
import { PageSecondaryHeaderTabLinks } from "./page-secondary-header-tab-links"
import { PageSecondaryHeaderTabLink } from "./page-secondary-header-tab-link"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderProps = {
  children?: React.ReactNode
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const pageSecondaryHeaderStyles = [
  "[--page-header-bottom-border-width:0.0625rem] border-b-(length:--page-header-bottom-border-width) border-warm-white-200",
  "min-h-16",
  "bg-white",
  "grid items-center"
]

const pageSecondaryHeaderContentStyles = [
  "[--page-header-padding:0.25rem] py-(length:--page-header-padding) px-4",
  "grid grid-cols-[1fr_auto] items-center gap-2 @6xl/page-header:grid-cols-[1fr_minmax(auto,1.5fr)_1fr]"
]

// =============================================================================
// Component
// =============================================================================

/**
 * Secondary page header content.
 * Contains breadcrumbs, steps, and actions.
 *
 * Compose with `PageSecondaryHeader.Breadcrumbs`, `PageSecondaryHeader.Steps`, `PageSecondaryHeader.Tabs`, `PageSecondaryHeader.CenterContent`, and `PageSecondaryHeader.Actions`.
 */
export function PageSecondaryHeaderRoot({
  children,
  className
}: PageSecondaryHeaderProps) {
  const { fullWidth } = usePageContext()
  return (
    <div className={cx(pageSecondaryHeaderStyles, className)}>
      <div
        className={cx(pageSecondaryHeaderContentStyles, {
          "mx-auto w-[min(100%,90rem)]": !fullWidth
        })}
      >
        {children}
      </div>
    </div>
  )
}

// =============================================================================
// Compound component
// =============================================================================

interface PageSecondaryHeaderComponent {
  (props: PageSecondaryHeaderProps): React.JSX.Element
  Heading: typeof PageSecondaryHeaderHeading
  Breadcrumbs: typeof PageSecondaryHeaderBreadcrumbs
  Breadcrumb: typeof PageSecondaryHeaderBreadcrumb
  Steps: typeof PageSecondaryHeaderSteps
  Step: typeof PageSecondaryHeaderStep
  CenterContent: typeof PageSecondaryHeaderCenterContent
  Actions: typeof PageSecondaryHeaderActions
  /**
   * Shared state provider for `PageSecondaryHeader.Tabs`. Re-export of Base UI's
   * `Tabs.Root`. Must wrap both the page header and the corresponding
   * `PageSecondaryHeader.TabPanel`s.
   */
  TabsRoot: typeof TabsPrimitive.Root
  Tabs: typeof PageSecondaryHeaderTabs
  Tab: typeof PageSecondaryHeaderTab
  /**
   * Content panel paired with a `PageSecondaryHeader.Tab`. Re-export of Base UI's
   * `Tabs.Panel`. Render in the page content, not inside the page header.
   */
  TabPanel: typeof TabsPrimitive.Panel
  TabLinks: typeof PageSecondaryHeaderTabLinks
  TabLink: typeof PageSecondaryHeaderTabLink
}

export const PageSecondaryHeader: PageSecondaryHeaderComponent = Object.assign(
  PageSecondaryHeaderRoot,
  {
    Heading: PageSecondaryHeaderHeading,
    Breadcrumbs: PageSecondaryHeaderBreadcrumbs,
    Breadcrumb: PageSecondaryHeaderBreadcrumb,
    Steps: PageSecondaryHeaderSteps,
    Step: PageSecondaryHeaderStep,
    CenterContent: PageSecondaryHeaderCenterContent,
    TabsRoot: TabsPrimitive.Root,
    Tabs: PageSecondaryHeaderTabs,
    Tab: PageSecondaryHeaderTab,
    TabPanel: TabsPrimitive.Panel,
    TabLinks: PageSecondaryHeaderTabLinks,
    TabLink: PageSecondaryHeaderTabLink,
    Actions: PageSecondaryHeaderActions
  } as const
)
